import axios from 'axios'
import { Certificate, getUserCertificates } from 'crypto-pro'
import { apiUrls } from 'modules/common/services/communication/urls'
import { useCallback, useState } from 'react'

export interface SignatureInfo {
  number: number
  subjectName: string
  serialNumber: string
  validTo: Date
  signingTime?: string
  issuerName?: string
  thumbprint?: string
  signerRole?: string
  comment?: string
}

export interface SignatureVerification {
  /** Подписанты, вычитанные из файла подписи */
  signatures: SignatureInfo[]
  /** true — КриптоПро подтвердил, что подписи соответствуют файлу */
  verified: boolean
  /** Причина, по которой проверка не прошла */
  error?: string
}

const UNKNOWN_SIGNATURE_FORMAT =
  'Файл подписи имеет неизвестный формат. Ожидается подпись CMS (PKCS#7) в кодировке DER или BASE64.'

/** Тег ASN.1 SEQUENCE — с него начинается любая подпись в DER */
const DER_SEQUENCE = 0x30

/**
 * Приводит файл подписи к тому виду, который понимает плагин КриптоПро — base64 от DER.
 *
 * КриптоАРМ и другие внешние утилиты по умолчанию сохраняют .sig в кодировке BASE64:
 * текстом с обрамлением "-----BEGIN CMS-----". Если такой файл закодировать в base64
 * как есть, получится base64 от base64 — плагин потом не находит в нём подписи
 * (0x800B0100) и не даёт заверить документ второй подписью (0x8009100E).
 */
export const normalizeSignatureBase64 = (base64Signature: string): string => {
  const cleaned = (base64Signature || '').replace(/\s/g, '')

  if (!cleaned) {
    throw new Error('Файл подписи пустой')
  }

  let binary = ''
  try {
    binary = atob(cleaned)
  } catch (e) {
    throw new Error(UNKNOWN_SIGNATURE_FORMAT)
  }

  if (binary.charCodeAt(0) === DER_SEQUENCE) {
    return cleaned
  }

  // Внутри — текстовый .sig, в нём лежит ещё один слой base64
  const payload = binary
    .split(/\r?\n/)
    .filter((line) => !line.trim().startsWith('-----'))
    .join('')
    .replace(/[^A-Za-z0-9+/=]/g, '')

  if (payload) {
    try {
      if (atob(payload).charCodeAt(0) === DER_SEQUENCE) {
        return payload
      }
    } catch (e) {
      // ниже — общая ошибка про неизвестный формат
    }
  }

  throw new Error(UNKNOWN_SIGNATURE_FORMAT)
}

/** Расшифровка кодов ошибок, которые возвращает плагин КриптоПро */
const CRYPTO_ERRORS: { [code: string]: string } = {
  '0x800B0100': 'в файле подписи не найдено ни одной подписи — файл повреждён или сохранён в неподдерживаемом формате',
  '0x8009100E': 'не удалось прочитать исходную подпись документа, поэтому к ней нельзя добавить свою',
  '0x800B0101': 'истёк срок действия сертификата',
  '0x800B010A': 'не удалось построить цепочку доверия сертификата — не установлены корневые сертификаты УЦ',
  '0x80090010': 'доступ к закрытому ключу запрещён',
  '0x8009000D': 'закрытый ключ недоступен: не подключён носитель или не введён пароль',
  '0x8010006E': 'операция отменена пользователем',
  '0x800B0109': 'корневой сертификат УЦ не установлен в доверенные',
  '0x80092012': 'не удалось проверить статус сертификата по списку отзыва',
}

/** Понятный текст ошибки КриптоПро вместо голого HRESULT-кода */
export const describeCryptoError = (e: any): string => {
  const message = (e && e.message) || String(e)
  const found = /0x[0-9A-Fa-f]{8}/.exec(message)

  if (!found) {
    return message
  }

  const explanation = CRYPTO_ERRORS[found[0].toUpperCase().replace('0X', '0x')]

  return explanation ? `${explanation} (${found[0]})` : message
}


export const useCryptoPro = () => {
  const [certs, setCerts] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchCerts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await getUserCertificates()

      setCerts(list)
      return list
    } catch (e) {
      setError((e as any).message)
      return []
    } finally {
      setLoading(false)
    }
  }, [])
  function prepareASN1String(text: string) {
    // 0x0C - это тег UTF8String в ASN.1
    // Нам нужно: [Тег][Длина][Контент]
    const utf8 = unescape(encodeURIComponent(text)) // Переводим в честный UTF-8
    const len = utf8.length

    // Формируем заголовок (простейший вариант для строк < 127 символов)
    const header = String.fromCharCode(0x0C) + String.fromCharCode(len)
    const fullStr = header + utf8

    // Кодируем в Base64, так как плагин лучше всего ест b64 через propset_Value
    return btoa(fullStr)
  }
  async function createHashLargeFile(arrayBuffer: any) {
    const cadesplugin = window.cadesplugin
    const hashedData = await cadesplugin.CreateObjectAsync("CAdESCOM.HashedData")

    await hashedData.propset_Algorithm(cadesplugin.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256)
    await hashedData.propset_DataEncoding(cadesplugin.CADESCOM_BASE64_TO_BINARY)

    const chunkSize = 1024 * 1024 // 1MB
    for (let offset = 0; offset < arrayBuffer.byteLength; offset += chunkSize) {
      const chunk = arrayBuffer.slice(offset, offset + chunkSize)
      const base64Chunk = arrayBufferToBase64(chunk)
      await hashedData.Hash(base64Chunk)
    }

    return await hashedData.Value
  }

  // Функция для скачивания файла из базы и сравнения хэшей
  const verifyFileHash = useCallback(async (file: File, fileId: string, fileName: string, baseUrl: string): Promise<boolean> => {

    try {
      // Скачиваем файл из базы (как в SignDialog)
      const fileUrl = baseUrl + apiUrls.application.files.content(fileId, fileName)

      const response = await axios.get(fileUrl, {
        responseType: 'arraybuffer',
        withCredentials: true
      })
      // Вычисляем хэш файла из базы
      const dbHash = await createHashLargeFile(response.data)
      // Вычисляем хэш загруженного файла 
      const uploadedArrayBuffer = await file.arrayBuffer()
      const uploadedHash = await createHashLargeFile(uploadedArrayBuffer)

      return uploadedHash === dbHash
    } catch (error) {
      console.info('Ошибка верификации:', error)
      return false
    }
  }, [])


  const createDetachedSignature = async (
    thumbprint: string,
    hash: string,
    type?: string,
    comment?: string
  ): Promise<string> => {
    const { cadesplugin } = window as any

    // Создаем объекты
    const oSignedData = await cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData")
    const oHashedData = await cadesplugin.CreateObjectAsync("CAdESCOM.HashedData")
    const oSigner = await cadesplugin.CreateObjectAsync("CAdESCOM.CPSigner")
    const authAttrs = await oSigner.AuthenticatedAttributes2

    // Получаем сертификат
    const certificates = await getUserCertificates()
    const certificate = certificates.find((c) => c.thumbprint === thumbprint)
    if (!certificate) throw new Error('Сертификат не найден')

    // Настраиваем подписанта
    await oSigner.propset_Certificate(certificate._cadesCertificate)

    // Добавляем атрибут времени подписи
    const timeAttr = await cadesplugin.CreateObjectAsync("CADESCOM.CPAttribute")
    await timeAttr.propset_Name(cadesplugin.CADESCOM_AUTHENTICATED_ATTRIBUTE_SIGNING_TIME)
    await timeAttr.propset_Value(new Date().toISOString())
    await authAttrs.Add(timeAttr)

    // Добавляем атрибут роли
    if (type) {
      const typeAttr = await cadesplugin.CreateObjectAsync("CADESCOM.CPAttribute")
      await typeAttr.propset_ValueEncoding(cadesplugin.CADESCOM_ENCODE_BASE64)

      let typeOID = await typeAttr.OID
      await typeOID.propset_Value("1.2.643.2.45.1.1.3") // OID для типа подписи

      await typeAttr.propset_Value(prepareASN1String(type))
      await authAttrs.Add(typeAttr)
    }

    // Добавляем атрибут комментария, если передан
    if (comment) {
      const commentAttr = await cadesplugin.CreateObjectAsync("CADESCOM.CPAttribute")
      await commentAttr.propset_ValueEncoding(cadesplugin.CADESCOM_ENCODE_BASE64)

      let commentOID = await commentAttr.OID
      await commentOID.propset_Value("1.2.643.2.45.1.1.1") // OID для комментария
      await commentAttr.propset_Value(prepareASN1String(comment))
      await authAttrs.Add(commentAttr)
    }

    await oSigner.propset_Options(cadesplugin.CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN)

    // Настраиваем хеш
    await oHashedData.propset_Algorithm(cadesplugin.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256)
    await oHashedData.SetHashValue(hash)

    // Создаем первую подпись
    return await oSignedData.SignHash(oHashedData, oSigner, cadesplugin.CADESCOM_PKCS7_TYPE)
  }

  // Добавляем подпись в файл подписей
  const addDetachedSignature = async (thumbprint: string, existingSignature: string, hash: string, type?: string, comment?: string) => {
    const { cadesplugin } = window as any

    const oSignedData = await cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData")
    const oHashedData = await cadesplugin.CreateObjectAsync("CAdESCOM.HashedData")
    const oSigner = await cadesplugin.CreateObjectAsync("CAdESCOM.CPSigner")
    const authAttrs = await oSigner.AuthenticatedAttributes2

    if (type) {
      const TypeOBJ = await cadesplugin.CreateObjectAsync("CAdESCOM.CPAttribute")
      await TypeOBJ.propset_ValueEncoding(cadesplugin.CADESCOM_ENCODE_BASE64)

      let TypeOID = await TypeOBJ.OID
      await TypeOID.propset_Value("1.2.643.2.45.1.1.3")
      await TypeOBJ.propset_Value(prepareASN1String(type))
      await authAttrs.Add(TypeOBJ)
    }
    if (comment) {
      const CommentOBJ = await cadesplugin.CreateObjectAsync("CAdESCOM.CPAttribute")
      await CommentOBJ.propset_ValueEncoding(cadesplugin.CADESCOM_ENCODE_BASE64)

      let CommentOID = await CommentOBJ.OID
      await CommentOID.propset_Value("1.2.643.2.45.1.1.1")
      await CommentOBJ.propset_Value(prepareASN1String(comment))
      await authAttrs.Add(CommentOBJ)
    }

    // Получаем сертификат
    const certificates = await getUserCertificates()
    const certificate = certificates.find((c) => c.thumbprint === thumbprint)
    if (!certificate) throw new Error('Сертификат не найден')

    // Настраиваем подписанта
    await oSigner.propset_Certificate(certificate._cadesCertificate)

    // Настраиваем хеш
    await oHashedData.propset_Algorithm(cadesplugin.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256)
    await oHashedData.SetHashValue(hash)

    // Загружаем существующую подпись
    await oSignedData.VerifyHash(oHashedData, normalizeSignatureBase64(existingSignature), cadesplugin.CADESCOM_PKCS7_TYPE)

    // Добавляем вторую подпись
    return await oSignedData.CoSignHash(oHashedData, oSigner, cadesplugin.CADESCOM_PKCS7_TYPE)
  }


  const getAttributeValue = async (attr: any): Promise<string> => {
    try {
      const { cadesplugin } = window as any

      await attr.propset_ValueEncoding(cadesplugin.CADESCOM_ENCODE_BASE64 ?? 1)

      const base64Value = await attr.Value
      if (!base64Value) return ''

      // Переводим Base64 в бинарную строку
      const binaryString = atob(base64Value.trim())

      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }


      let dataOffset = 2
      if (bytes[1] & 0x80) {
        // Если длина занимает более 1 байта 
        const numLengthBytes = bytes[1] & 0x7F
        dataOffset = 2 + numLengthBytes
      }

      // Извлекаем только полезную нагрузку (массив байт текста)
      const textBytes = bytes.subarray(dataOffset)

      // байты в текст UTF-8
      const decoder = new TextDecoder('utf-8')
      const decodedText = decoder.decode(textBytes)

      // Очищаем от возможных непечатных управляющих символов ASN.1 на стыках
      // eslint-disable-next-line no-control-regex
      return decodedText.replace(/[\x00-\x1F\x7F]/g, '').trim()

    } catch (error) {
      console.info('Ошибка парсинга атрибута:', error)
      return ''
    }
  }
  const extractSignaturesInfo = useCallback(async (
    base64Signature: string,
    base64Content: string
  ): Promise<SignatureVerification> => {
    const { cadesplugin } = window as any

    if (!cadesplugin) {
      throw new Error('КриптоПро плагин не установлен. Пожалуйста, установите КриптоПро CSP и плагин для браузера.')
    }

    const signature = normalizeSignatureBase64(base64Signature)

    const oSignedData = await cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData")

    // Указываем кодировку
    await oSignedData.propset_ContentEncoding(cadesplugin.CADESCOM_BASE64_TO_BINARY)

    // Загружаем исходный контент для отделенной подписи
    await oSignedData.propset_Content(base64Content)

    // Проверяем подпись. Ошибку не прячем — она и есть результат проверки,
    // но подписантов из файла всё равно пробуем показать.
    let verified = true
    let verificationError: string | undefined = undefined

    try {
      await oSignedData.VerifyCades(signature, cadesplugin.CADESCOM_CADES_BES, true)
    } catch (verifyErr) {
      verified = false
      verificationError = describeCryptoError(verifyErr)
    }

    // Получаем коллекцию подписантов
    let signersCount = 0
    let oSigners: any = null

    try {
      oSigners = await oSignedData.Signers
      signersCount = await oSigners.Count
    } catch (signersErr) {
      // Подпись не разобралась — показывать нечего, отдаём причину проверки
      return {
        signatures: [],
        verified: false,
        error: verificationError || describeCryptoError(signersErr)
      }
    }

    const signaturesInfo: SignatureInfo[] = []

    for (let i = 1; i <= signersCount; i++) {
      const signer = await oSigners.Item(i)
      const cert = await signer.Certificate

      const subjectName = await cert.SubjectName
      const validToStr = await cert.ValidToDate
      const serialNumber = await cert.SerialNumber

      let thumbprint = ''
      try {
        thumbprint = await cert.Thumbprint
      } catch (e) {
      }

      let issuerName = ''
      try {
        issuerName = await cert.IssuerName
      } catch (e) {
      }

      let signingTime: string | undefined = undefined
      try {
        const time = await signer.SigningTime
        if (time) signingTime = time
      } catch (e) {
      }

      // Извлекаем атрибуты подписи (роль и комментарий)
      let signerRole = ''
      let comment = ''

      try {
        const authenticatedAttributes = await signer.AuthenticatedAttributes2
        const count = await authenticatedAttributes.Count

        for (let j = 1; j <= count; j++) {
          const attr = await authenticatedAttributes.Item(j)
          const oid = await attr.OID
          const oidValue = await oid.Value


          // OID для комментария
          if (oidValue === '1.2.643.2.45.1.1.1') {
            comment = await getAttributeValue(attr)
          }

          // OID для роли
          if (oidValue === '1.2.643.2.45.1.1.3') {
            signerRole = await getAttributeValue(attr)
          }
        }
      } catch (e) {
      }



      signaturesInfo.push({
        number: i,
        subjectName,
        serialNumber,
        validTo: new Date(validToStr),
        signingTime,
        issuerName,
        thumbprint,
        signerRole,
        comment
      })
    }

    return {
      signatures: signaturesInfo,
      verified,
      error: verificationError
    }
  }, [])

  // проверка подписи и получение информации
  const verifySignature = useCallback(async (
    base64Signature: string,
    fileArrayBuffer: ArrayBuffer
  ): Promise<SignatureVerification> => {
    const base64Content = arrayBufferToBase64(fileArrayBuffer)
    return await extractSignaturesInfo(base64Signature, base64Content)
  }, [extractSignaturesInfo])

  // Вспомогательная функция для конвертации ArrayBuffer в Base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }


  const signBase64 = useCallback(async (
    thumbprint: string,
    arrBuffer: any,
    existingSignature?: string,
    type?: string,
    comment?: string
  ): Promise<string | undefined> => {
    try {

      const hash = await createHashLargeFile(arrBuffer)
      if (!existingSignature) {
        // Первый подписант - используем createDetachedSignatureWithMeta
        return await createDetachedSignature(thumbprint, hash, type, comment)
      } else {
        // Второй и последующие - используем addDetachedSignatureWithMeta
        return await addDetachedSignature(thumbprint, existingSignature, hash, type, comment)
      }
    } catch (e) {
      throw new Error(`Ошибка подписи: ${describeCryptoError(e)}`)
    }
    // eslint-disable-next-line
  }, [])

  return {
    certs, fetchCerts, signBase64, verifyFileHash, verifySignature,
    extractSignaturesInfo, loading, error
  }
}
